import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  canAssignRoles,
  canReviewAccessRequests,
  canSeeInventory,
  type AppRole,
} from '@infras/shared-types';
import { CrumbProvider } from '../lib/crumb';
import { DOC_SECTIONS } from '../lib/docs';
import { useFreshness } from '../lib/useFreshness';
import { Icon, type IconName } from './Icon';
import { PendingAccessBadge } from './PendingAccessBadge';
import { TopBar } from './TopBar';

/** Sidebar counts — see the `navCounts` resolver for why this isn't the list queries. */
const NAV_COUNTS = gql`
  query NavCounts {
    navCounts {
      repos
      clusters
      secrets
    }
  }
`;

const COLLAPSE_KEY = 'infras.sidebar';

type Counts = { repos: number; clusters: number; secrets: number };

interface NavItem {
  to: string;
  label: string;
  /** Sidebar icon. Replaces the 2-char monogram the first cut used because the app
   *  had no icon set; `Icon` now provides one with no dependency. */
  icon: IconName;
  /** Which count to show, if any. */
  count?: keyof Counts;
  /** Renders its own trailing element (pending badge, freshness dot). */
  slot?: 'pending' | 'freshness';
  /** Show DOC_SECTIONS beneath this item while the route is active. `DocsPage`
   *  renders no section menu of its own — it documents that the menu lives in
   *  this sidebar — so dropping this makes all four doc sections URL-only. */
  docsSubnav?: true;
  can: (role: AppRole) => boolean;
}

const EVERYONE = () => true;

/**
 * The nav, grouped. Gates are the SAME predicates the API enforces
 * (`packages/shared-types/src/rbac.ts`) — this is UX; RolesGuard is the boundary.
 *
 * The four groups come from the redesign handoff. **Network is kept as a fifth
 * group**: the handoff's table omitted Atlas IPs, Agents and the three VPN views,
 * and rendering it literally would have left five live routes reachable only by
 * typing the URL. Atlas IPs + Agents fold into Operations for the same reason.
 *
 * ORDER IS DELIBERATE and is what renders. Reordering this array reorders the
 * sidebar — there is no separate sort.
 *
 *  - The FIRST group has an empty label and renders as a pinned block above a
 *    separator. Access requests and Docs & MCP are the only two items every role
 *    can open, and they used to sit inside Operations and Reference — so a
 *    `new_member`, who is this app's main filer of requests, got a sidebar
 *    reading "OPERATIONS" with one item under it and "REFERENCE" with one under
 *    that. Two group labels out of the app's internal vocabulary, wrapped around
 *    everything they are allowed to do. Unlabelled, they read as "things you can
 *    always do", which is what they are. They stay first for every other role
 *    too: Access requests is the most-used cross-role surface in the app.
 *  - Administration sits LAST. It is the group the fewest people can open and
 *    the one nobody visits to do their actual work.
 */
const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: '',
    items: [
      { to: '/access', label: 'Access requests', icon: 'access', slot: 'pending', can: EVERYONE },
      { to: '/docs', label: 'Docs & MCP', icon: 'docs', docsSubnav: true, can: EVERYONE },
    ],
  },
  {
    group: 'Inventory',
    items: [
      // `/overview` had a route and a crumb but no nav entry, so it was reachable
      // only by clicking the brand — which is not a thing anyone discovers. It is
      // the page that answers "can I trust what I'm about to read", so it belongs
      // at the top of the group rather than hidden behind the logo.
      { to: '/overview', label: 'Overview', icon: 'grid', can: canSeeInventory },
      { to: '/repos', label: 'Repos', icon: 'repo', count: 'repos', can: canSeeInventory },
      { to: '/clusters', label: 'Clusters', icon: 'cluster', count: 'clusters', can: canSeeInventory },
      { to: '/secrets', label: 'Secrets', icon: 'key', count: 'secrets', can: canSeeInventory },
    ],
  },
  {
    group: 'Operations',
    items: [
      { to: '/scheduler', label: 'Scheduler', icon: 'clock', slot: 'freshness', can: canSeeInventory },
      { to: '/activity', label: 'Activity', icon: 'monitor', can: canSeeInventory },
      { to: '/atlas-whitelist', label: 'Atlas IPs', icon: 'shield', can: canSeeInventory },
      { to: '/agents', label: 'Agents', icon: 'run', can: canSeeInventory },
    ],
  },
  {
    // Deploy catalog is alone here now that Docs is pinned. Kept as its own group
    // rather than folded into Inventory because it is not scanned truth — it is
    // the hand-maintained recipe of record, and filing it under Inventory would
    // imply a scanner stands behind it.
    group: 'Reference',
    items: [
      { to: '/catalog', label: 'Deploy catalog', icon: 'catalog', can: canSeeInventory },
      // EVERYONE, unlike the rest of Reference. This page is a vocabulary — error
      // codes and the customer copy that goes with each — not inventory. A new
      // member fielding their first ticket is exactly who needs to look a code up,
      // and gating it behind a role assignment is a worse failure than showing it.
      { to: '/error-codes', label: 'Error dictionary', icon: 'docs', can: EVERYONE },
    ],
  },
  {
    group: 'Network',
    items: [
      { to: '/vpn/peers', label: 'Peers', icon: 'account', can: canSeeInventory },
      { to: '/vpn/subnets', label: 'Subnets', icon: 'cluster', can: canSeeInventory },
      // SOC2 audit-of-record — root only, matching @RequireRole('root') on the query.
      { to: '/vpn/connection-audit', label: 'Connection audit', icon: 'monitor', can: canAssignRoles },
    ],
  },
  {
    group: 'Administration',
    items: [
      { to: '/settings/team', label: 'Users & roles', icon: 'users', can: canAssignRoles },
      {
        to: '/settings/reviewers',
        label: 'Access reviewers',
        icon: 'shield',
        can: canReviewAccessRequests,
      },
      {
        to: '/settings/questions',
        label: 'Request questions',
        icon: 'question',
        can: canReviewAccessRequests,
      },
    ],
  },
];

export function AppShell({
  role,
  email,
  onLogout,
  children,
}: {
  role: AppRole;
  email: string;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const inDocs = useLocation().pathname.startsWith('/docs');
  const inventory = canSeeInventory(role);
  const freshness = useFreshness(!inventory);
  const { data } = useQuery<{ navCounts: Counts }>(NAV_COUNTS, { skip: !inventory });
  const counts = data?.navCounts;

  // Persist OUTSIDE the updater — React may invoke an updater more than once
  // (StrictMode does in dev) and requires it to be pure.
  const toggle = () => {
    const next = !collapsed;
    localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    setCollapsed(next);
  };

  return (
    <CrumbProvider>
      <div className={`app-shell${collapsed ? ' app-shell--collapsed' : ''}`}>
        <TopBar
          email={email}
          role={role}
          onLogout={onLogout}
          showInventory={inventory}
        />
        <div className="app-body">
          <aside className="sidebar" aria-label="Main navigation">
            <nav className="side-nav">
              {NAV.map(({ group, items }) => {
                const visible = items.filter((i) => i.can(role));
                if (!visible.length) return null;
                return (
                  // An empty group name is the pinned block: no label, and a rule
                  // below it so it reads as pinned rather than as the head of
                  // whatever group follows.
                  <div className={`nav-group${group ? '' : ' nav-group--pinned'}`} key={group}>
                    {group && <div className="nav-label">{group}</div>}
                    {visible.map((item) => (
                      <div key={item.to}>
                        <NavLink
                          to={item.to}
                          // `end` only on '/', or every route would mark Overview active
                          end={item.to === '/'}
                          className="nav-item"
                          title={collapsed ? item.label : undefined}
                          // The icon is aria-hidden, so the collapsed rail needs
                          // the name here or a screen reader gets an empty link.
                          aria-label={collapsed ? item.label : undefined}
                        >
                          <Icon name={item.icon} className="nav-icon" />
                          <span className="nav-text">{item.label}</span>
                          {item.count && counts && (
                            <span className="nav-count">{counts[item.count]}</span>
                          )}
                          {/* Not gated on `inventory` any more: the badge counts
                              your OWN pending requests too, and a new member with
                              one in flight is exactly who needs to see it. */}
                          {item.slot === 'pending' && <PendingAccessBadge />}
                          {item.slot === 'freshness' && (
                            <span
                              className={`freshness-dot freshness-dot--${freshness.tone}`}
                              title={freshness.label}
                            />
                          )}
                        </NavLink>
                        {item.docsSubnav && inDocs && !collapsed && (
                          <div className="side-subnav">
                            {DOC_SECTIONS.map((s) => (
                              <NavLink
                                key={s.slug}
                                to={`/docs/${s.slug}`}
                                className="side-subnav-link"
                              >
                                {s.label}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </nav>
            <div className="sidebar-foot">
              <button
                className="sidebar-collapse"
                onClick={toggle}
                aria-expanded={!collapsed}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <Icon name="chevron" className={`collapse-chevron${collapsed ? '' : ' collapse-chevron--flip'}`} />
                <span className="nav-text">Collapse</span>
              </button>
            </div>
          </aside>
          <main className="app-main">{children}</main>
        </div>
      </div>
    </CrumbProvider>
  );
}
